import React, { useState } from 'react';
import { Plus, Eye, Edit, Trash2, Image, Folder, FolderPlus, ChevronRight, Home } from 'lucide-react';
import MediaViewer from '../../../components/MediaViewer';
import api from '../../../utils/api';

const GallerySection = ({
  gallery,
  galleryLoading,
  galleryCategory,
  currentFolder,
  folders,
  setEditingImage,
  setGalleryForm,
  setSelectedImageFile,
  setShowGalleryModal,
  handleGalleryCategoryChange,
  handleEnterFolder,
  handleExitFolder,
  handleViewImage,
  handleEditImage,
  handleDeleteImage,
  // Modal props
  showGalleryModal,
  editingImage,
  galleryForm,
  selectedImageFile,
  handleGalleryInputChange,
  handleImageFileChange,
  showImageViewModal,
  viewingImage,
  closeImageViewModal,
  // Callback functions
  onImageUploaded,
  onMultipleImagesUploaded,
  onImageUpdated,
  showCustomDialog,
  setGalleryLoading,
  // Folder management
  setFolders,
  fetchFolders
}) => {
  // Local state for folder functionality
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [folderForm, setFolderForm] = useState({
    name: '',
    category: 'others'
  });
  // eslint-disable-next-line no-unused-vars
  const [editingFolder, setEditingFolder] = useState(null);

  // Folders are now managed by parent component

  // Handle folder form input changes
  const handleFolderInputChange = (e) => {
    const { name, value } = e.target;
    setFolderForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle folder creation
  const handleCreateFolder = async (e) => {
    e.preventDefault();
    setFoldersLoading(true);

    try {
      const response = await api.post('/api/gallery/folders', folderForm);
      setFolders(prev => [response.data, ...prev]);
      setShowCreateFolderModal(false);
      setFolderForm({ name: '', category: 'others' });
      showCustomDialog('Folder created successfully!');
    } catch (error) {
      console.error('Error creating folder:', error);
      const message = error.response?.data?.message || 'Failed to create folder';
      showCustomDialog(message, 'error');
    } finally {
      setFoldersLoading(false);
    }
  };

  // Handle folder deletion
  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('Are you sure you want to delete this folder?')) {
      return;
    }

    try {
      await api.delete(`/api/gallery/folders/${folderId}`);
      setFolders(prev => prev.filter(folder => folder._id !== folderId));
      showCustomDialog('Folder deleted successfully!');
    } catch (error) {
      console.error('Error deleting folder:', error);
      const message = error.response?.data?.message || 'Failed to delete folder';
      showCustomDialog(message, 'error');
    }
  };

  // Folder navigation is now handled by parent component

  // Helper function for direct Cloudinary upload
  const uploadSingleImage = async (file, category) => {
    try {
      // Extract filename without extension for title
      const title = file.name.replace(/\.[^/.]+$/, "");

      // Step 1: Get upload signature
      const signatureResponse = await api.post('/api/gallery/upload-signature', {
        title: title,
        category: category
      });

      const { signature, timestamp, cloudName, apiKey, uploadParams } = signatureResponse.data;

      // Step 2: Upload directly to Cloudinary
      const cloudinaryFormData = new FormData();
      cloudinaryFormData.append('file', file);
      cloudinaryFormData.append('signature', signature);
      cloudinaryFormData.append('timestamp', timestamp);
      cloudinaryFormData.append('api_key', apiKey);

      // Add upload parameters
      Object.keys(uploadParams).forEach(key => {
        if (key !== 'timestamp') {
          cloudinaryFormData.append(key, uploadParams[key]);
        }
      });

      const cloudinaryResponse = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
        method: 'POST',
        body: cloudinaryFormData
      });

      if (!cloudinaryResponse.ok) {
        throw new Error('Failed to upload image to Cloudinary');
      }

      const cloudinaryResult = await cloudinaryResponse.json();

      // Step 3: Save image info to database
      const response = await api.post('/api/gallery/save-image', {
        title: title,
        description: '', // Empty description
        category: category,
        imageUrl: cloudinaryResult.secure_url,
        publicId: cloudinaryResult.public_id,
        folderId: currentFolder?._id || null
      });

      return response.data;
    } catch (error) {
      console.error('Gallery upload error:', error);
      throw new Error(`Failed to upload ${file.name}. Please try again.`);
    }
  };

  // Helper function to upload multiple images
  const uploadMultipleImages = async (files, category) => {
    const uploadedImages = [];
    const failedUploads = [];

    for (const file of files) {
      try {
        const uploadedImage = await uploadSingleImage(file, category);
        uploadedImages.push(uploadedImage);
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
        failedUploads.push(file.name);
      }
    }

    return { uploadedImages, failedUploads };
  };

  // Handle form submission
  const handleGallerySubmit = async (e) => {
    e.preventDefault();
    setUploadLoading(true);

    try {
      if (editingImage) {
        // Update existing image metadata
        const response = await api.put(`/api/gallery/${editingImage._id}`, {
          title: galleryForm.title,
          description: galleryForm.description,
          category: galleryForm.category
        });
        onImageUpdated(response.data);
        setEditingImage(null);
        showCustomDialog('Image updated successfully!');
      } else {
        // Upload multiple images
        if (!selectedImageFile || selectedImageFile.length === 0) {
          showCustomDialog('Please select at least one image file', 'error');
          setUploadLoading(false);
          return;
        }

        // Use folder category if inside a folder, otherwise use form category
        const categoryToUse = currentFolder ? currentFolder.category : galleryForm.category;
        const { uploadedImages, failedUploads } = await uploadMultipleImages(selectedImageFile, categoryToUse);

        // Update gallery with new images (add all at once to avoid state conflicts)
        if (uploadedImages.length > 0) {
          onMultipleImagesUploaded(uploadedImages);
        }

        // Show success/error messages
        if (uploadedImages.length > 0 && failedUploads.length === 0) {
          showCustomDialog(`${uploadedImages.length} image${uploadedImages.length !== 1 ? 's' : ''} uploaded successfully!`);
        } else if (uploadedImages.length > 0 && failedUploads.length > 0) {
          showCustomDialog(`${uploadedImages.length} image${uploadedImages.length !== 1 ? 's' : ''} uploaded successfully. ${failedUploads.length} failed: ${failedUploads.join(', ')}`, 'error');
        } else {
          showCustomDialog(`Failed to upload images: ${failedUploads.join(', ')}`, 'error');
        }
      }

      // Reset form
      setGalleryForm({
        title: '',
        description: '',
        category: 'others'
      });
      setSelectedImageFile(null);
      setShowGalleryModal(false);
    } catch (error) {
      console.error('Error saving images:', error);
      showCustomDialog('Failed to save images', 'error');
    } finally {
      setUploadLoading(false);
    }
  };

  const galleryCategories = [
    { value: 'all', label: 'All' },
    { value: 'perunnal', label: 'Perunnal' },
    { value: 'consecration', label: 'Consecration' },
    { value: 'construction', label: 'Construction' },
    { value: 'holyweek', label: 'Holy Week' },
    { value: 'others', label: 'Others' }
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div className="flex flex-col">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Manage Gallery</h2>
          {/* Breadcrumb */}
          {currentFolder && (
            <div className="flex items-center text-sm text-gray-600 mt-1">
              <button
                onClick={handleExitFolder}
                className="flex items-center hover:text-gray-800"
              >
                <Home className="w-4 h-4 mr-1" />
                Gallery
              </button>
              <ChevronRight className="w-4 h-4 mx-1" />
              <span className="font-medium">{currentFolder.name}</span>
            </div>
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          {!currentFolder && (
            <button
              onClick={() => {
                setEditingFolder(null);
                setFolderForm({
                  name: '',
                  category: galleryCategory !== 'all' ? galleryCategory : 'others'
                });
                setShowCreateFolderModal(true);
              }}
              className="bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center text-sm sm:text-base">
              <FolderPlus className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              Create Folder
            </button>
          )}
          <button
            onClick={() => {
              setEditingImage(null);
              setGalleryForm({
                title: '',
                description: '',
                category: currentFolder ? currentFolder.category : 'others'
              });
              setSelectedImageFile(null);
              setShowGalleryModal(true);
            }}
            className="bg-green-600 text-white px-3 sm:px-4 py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center text-sm sm:text-base">
            <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
            Add Image
          </button>
        </div>
      </div>

      {/* Category Filter - only show when not inside a folder */}
      {!currentFolder && (
        <div className="flex flex-wrap justify-center gap-4">
          {galleryCategories.map((category) => (
            <button
              key={category.value}
              onClick={() => handleGalleryCategoryChange(category.value)}
              className={`px-4 py-2 rounded-full font-medium transition-colors ${
                galleryCategory === category.value
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {category.label}
            </button>
          ))}
        </div>
      )}

      {/* Single Loading State for Everything */}
      {galleryLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading gallery content...</p>
        </div>
      ) : (
        <>
          {/* Folders Section - only show when not inside a folder */}
          {!currentFolder && folders.length > 0 && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Folders</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 mb-6">
                {folders.map((folder) => (
                  <div
                    key={folder._id}
                    className="bg-white rounded-lg shadow hover:shadow-md transition-shadow cursor-pointer border"
                  >
                    <div className="p-4">
                      <div className="flex flex-col items-center space-y-2">
                        <div
                          onClick={() => handleEnterFolder(folder)}
                          className="flex flex-col items-center space-y-2 flex-1"
                        >
                          <Folder className="w-8 h-8 text-blue-600" />
                          <span className="text-sm font-medium text-gray-900 text-center break-words">
                            {folder.name}
                          </span>
                          <span className="text-xs text-gray-500">
                            {folder.imageCount} image{folder.imageCount !== 1 ? 's' : ''}
                          </span>
                        </div>
                        {/* Only show delete button if folder is empty */}
                        {folder.imageCount === 0 && (
                          <div className="flex space-x-1 pt-1 border-t border-gray-100 w-full justify-center">
                            <button
                              onClick={() => handleDeleteFolder(folder._id)}
                              className="text-red-600 hover:text-red-700 p-1"
                              title="Delete Folder"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Images Section */}
          {gallery.length > 0 ? (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {currentFolder ? `Images in "${currentFolder.name}"` : 'Images'}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {gallery.map((image) => (
                  <div key={image._id} className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="relative">
                      <img
                        src={image.src}
                        alt={image.alt}
                        className="w-full h-48 object-cover"
                      />
                      <div className="absolute top-2 right-2 flex space-x-1">
                        <button
                          onClick={() => handleViewImage(image)}
                          className="bg-green-600 text-white p-1 rounded-full hover:bg-green-700 transition-colors">
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEditImage(image)}
                          className="bg-blue-600 text-white p-1 rounded-full hover:bg-blue-700 transition-colors">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteImage(image._id)}
                          className="bg-red-600 text-white p-1 rounded-full hover:bg-red-700 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 mb-1">{image.title}</h3>
                      {image.description && (
                        <p className="text-sm text-gray-600 mb-2">{image.description}</p>
                      )}
                      <div className="flex justify-between items-center">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          image.category === 'perunnal' ? 'bg-blue-100 text-blue-800' :
                          image.category === 'consecration' ? 'bg-green-100 text-green-800' :
                          image.category === 'construction' ? 'bg-yellow-100 text-yellow-800' :
                          image.category === 'holyweek' ? 'bg-purple-100 text-purple-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {image.category}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(image.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : gallery.length === 0 && folders.length === 0 ? (
            <div className="text-center py-12">
              <Image className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No Images</h3>
              <p className="text-gray-500">Upload your first image to get started.</p>
            </div>
          ) : null}
        </>
      )}

    {/* Gallery Modal */}
    {showGalleryModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-md mx-auto max-h-[97%] overflow-y-auto">
          <h3 className="text-lg font-semibold mb-4">{editingImage ? 'Edit Image' : 'Upload Images'}</h3>
          <form onSubmit={handleGallerySubmit} className="space-y-4">
            {editingImage && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={galleryForm.title}
                    onChange={handleGalleryInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    name="description"
                    value={galleryForm.description}
                    onChange={handleGalleryInputChange}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="Optional description"
                  />
                </div>
              </>
            )}
            {/* Only show category selector when not inside a folder */}
            {!currentFolder && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                <select
                  name="category"
                  value={galleryForm.category}
                  onChange={handleGalleryInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="perunnal">Perunnal</option>
                  <option value="consecration">Consecration</option>
                  <option value="construction">Construction</option>
                  <option value="holyweek">Holy Week</option>
                  <option value="others">Others</option>
                </select>
              </div>
            )}

            {/* Show folder category info when inside a folder */}
            {currentFolder && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-600">
                  {currentFolder.category.charAt(0).toUpperCase() + currentFolder.category.slice(1)}
                </div>
              </div>
            )}
            {!editingImage && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Images *</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageFileChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Select multiple images. Supported formats: JPEG, PNG, GIF, WebP (max 20MB each)
                </p>
                {selectedImageFile && selectedImageFile.length > 0 && (
                  <div className="mt-2 text-sm text-green-600">
                    {selectedImageFile.length} file{selectedImageFile.length !== 1 ? 's' : ''} selected
                  </div>
                )}
              </div>
            )}
            <div className="flex space-x-3 pt-4">
              <button
                type="submit"
                disabled={uploadLoading}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {uploadLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {editingImage ? 'Updating...' : 'Uploading...'}
                  </>
                ) : (
                  editingImage ? 'Update Image' : 'Upload Image'
                )}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowGalleryModal(false);
                  setEditingImage(null);
                  setGalleryForm({
                    title: '',
                    description: '',
                    category: 'others'
                  });
                  setSelectedImageFile(null);
                }}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    )}

    {/* MediaViewer for Image Viewing */}
    <MediaViewer
      isOpen={showImageViewModal && !!viewingImage}
      onClose={closeImageViewModal}
      resource={viewingImage ? {
        resource_url: viewingImage.src,
        resource_name: viewingImage.title,
        file_name: viewingImage.src.split('/').pop()
      } : null}
      title={viewingImage?.title || ''}
    />

    {/* Create Folder Modal */}
    {showCreateFolderModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-md mx-auto">
          <h3 className="text-lg font-semibold mb-4">Create New Folder</h3>
          <form onSubmit={handleCreateFolder} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Folder Name *</label>
              <input
                type="text"
                name="name"
                value={folderForm.name}
                onChange={handleFolderInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                placeholder="Enter folder name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
              <select
                name="category"
                value={folderForm.category}
                onChange={handleFolderInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="perunnal">Perunnal</option>
                <option value="consecration">Consecration</option>
                <option value="construction">Construction</option>
                <option value="holyweek">Holy Week</option>
                <option value="others">Others</option>
              </select>
            </div>
            <div className="flex space-x-3 pt-4">
              <button
                type="submit"
                disabled={foldersLoading}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {foldersLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating...
                  </>
                ) : (
                  'Create Folder'
                )}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateFolderModal(false);
                  setFolderForm({ name: '', category: 'others' });
                }}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    )}
    </div>
  );
};

export default GallerySection;